/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: aiProvidersApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API client functions for AI Providers module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';

export interface AiProvider {
  id: number;
  uuid: string;
  domain_id: number;
  provider: string;
  name: string;
  api_key: string;
  base_url?: string | null;
  organization?: string | null;
  api_key_type?: string | null;
  capabilities?: string[] | null;
  voices_cache?: Record<string, unknown> | null;
  status: boolean;
  created_at: string;
  created_by?: number | null;
  updated_at?: string | null;
  updated_by?: number | null;
  supported_types?: string[];
}

export interface AiProviderListResponse {
  success: boolean;
  data: AiProvider[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AiProviderResponse {
  success: boolean;
  data: AiProvider;
  message?: string;
}

export interface ConnectionTestResponse {
  success: boolean;
  message: string;
  data?: {
    provider?: string;
    name?: string;
    response_time?: number;
    base_url?: string;
    models_available?: number;
    organization?: string;
  };
}

/**
 * Get all AI providers with pagination and filtering
 */
export async function getAiProviders(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  tenantSlug?: string;
  userId?: string;
  domainId?: number;
}): Promise<AiProviderListResponse> {
  const queryParams = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder
  });

  if (params.search?.trim()) {
    queryParams.set('search', params.search.trim());
  }

  if (params.domainId != null) {
    queryParams.set('domainId', String(params.domainId));
  }

  const response = await fetch(`${buildApiUrl('/api/ai/providers')}?${queryParams.toString()}`, {
    headers: getApiHeaders(params.tenantSlug, params.userId)
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Get single AI provider by ID
 */
export async function getAiProviderById(
  id: string | number,
  tenantSlug?: string,
  userId?: string
): Promise<AiProviderResponse> {
  const response = await fetch(buildApiUrl(`/api/ai/providers/${id}`), {
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Create new AI provider
 */
export async function createAiProvider(
  data: {
    domain_id: number;
    provider: string;
    name: string;
    api_key: string;
    base_url?: string | null;
    organization?: string | null;
    api_key_type?: string | null;
    capabilities?: string[];
    voices_cache?: Record<string, unknown> | null;
    status?: boolean;
  },
  tenantSlug?: string,
  userId?: string
): Promise<AiProviderResponse> {
  const response = await fetch(buildApiUrl('/api/ai/providers'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Update AI provider
 */
export async function updateAiProvider(
  id: string | number,
  data: {
    provider?: string;
    name?: string;
    api_key?: string;
    base_url?: string | null;
    organization?: string | null;
    api_key_type?: string | null;
    capabilities?: string[];
    voices_cache?: Record<string, unknown> | null;
    status?: boolean;
  },
  tenantSlug?: string,
  userId?: string
): Promise<AiProviderResponse> {
  const response = await fetch(buildApiUrl(`/api/ai/providers/${id}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Delete AI provider
 */
export async function deleteAiProvider(
  id: string | number,
  tenantSlug?: string,
  userId?: string
): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/ai/providers/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
}

/**
 * Test AI provider connection (with provided data)
 */
export async function testAiProviderConnection(
  data: {
    domain_id: number;
    provider: string;
    name: string;
    api_key: string;
    base_url?: string | null;
    organization?: string | null;
    api_key_type?: string | null;
    capabilities?: string[];
    voices_cache?: Record<string, unknown> | null;
    status?: boolean;
  },
  tenantSlug?: string,
  userId?: string
): Promise<ConnectionTestResponse> {
  const response = await fetch(buildApiUrl('/api/ai/providers/test'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Test AI provider connection (by ID)
 */
export async function testAiProviderConnectionById(
  id: string | number,
  tenantSlug?: string,
  userId?: string
): Promise<ConnectionTestResponse> {
  const response = await fetch(buildApiUrl(`/api/ai/providers/${id}/test`), {
    method: 'POST',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({})
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Refresh capabilities cache
 */
export async function refreshAiProviderCapabilities(
  id: string | number,
  tenantSlug?: string,
  userId?: string
): Promise<AiProviderResponse> {
  const response = await fetch(buildApiUrl(`/api/ai/providers/${id}/refresh-capabilities`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({})
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Refresh voices cache (Azure TTS only)
 */
export async function refreshAiProviderVoices(
  id: string | number,
  tenantSlug?: string,
  userId?: string
): Promise<AiProviderResponse> {
  const response = await fetch(buildApiUrl(`/api/ai/providers/${id}/refresh-voices`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({})
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

